// src/components/common/companyLink.js
//
// THE ONE RULE FOR LINKING A DOCUMENT TO AN AUTOTASK COMPANY.
//
// ── WHY IT LIVES IN common/ AND NOT quotes/ ────────────────────────────────
// It shipped as quotes/quoteCompany.js, serving the quote builder alone. When
// proposals needed the same behaviour there were two options: import a module
// called "quoteCompany" into a proposal, or write the rule a second time. The
// second is how the two would drift — and a duplicated company rule is the
// exact problem this whole line of work exists to fix, so a second copy would
// have been the wrong lesson to learn from it.
//
// ── THE RULE ───────────────────────────────────────────────────────────────
//   The NAME is the truth, and it is free text.
//   The ID is an optional link, and it exists only on an explicit pick.
//
// Craig needs to quote and propose to prospects, referrals and businesses that
// were never onboarded, so the company box cannot be a dropdown over Autotask.
// But it must not become pure free text either: Proposals made exactly that
// change, and CLAUDE.md records the cost — zero of twelve real proposals carry
// an Autotask companyId, so the onboarding pipeline cannot resolve a company on
// its own and has to be told one on every single run.
//
// ── ⚠ THE TWO DOCUMENT TYPES STORE THE NAME IN DIFFERENT PLACES ────────────
// This is why there are two patch helpers rather than one, and the asymmetry is
// deliberate:
//
//   QUOTE     companyName + companyId, both TOP LEVEL on the record.
//   PROPOSAL  content.customerName is the display truth and already renders the
//             organisation name on the cover, the page headers and the body
//             copy. Only companyId is added, top level. A top-level
//             `companyName` beside content.customerName would be TWO NAMES for
//             one organisation, drifting the moment either is edited — the
//             two-sources-of-truth failure this codebase has already been bitten
//             by twice. Do not add one.
//
// Both go through companyLinkId(), so the safety rule below has exactly one
// definition no matter which document is being edited.

/**
 * The id to store for a company box that now reads `match`.
 *
 * @param {object|null} match  the Autotask company ONLY when explicitly picked
 *                             from the dropdown; null on every keystroke
 *
 * ⚠ RETURNS "" WHENEVER THERE IS NO MATCH, and that is the entire point of
 * this function rather than a spread-and-override at each call site. Picking
 * "Hedland Motors" and then typing over it leaves a field that SAYS one company
 * while still holding another's id — and the id is what gets filed against.
 * That is the cross-client mis-file class: on a quote it pushes to the wrong
 * company's Autotask record; on a proposal it is worse, because Stage A writes
 * a CONTRACT and financial service lines against it.
 *
 * Clearing is the safe direction. The worst case is Craig re-picking a company;
 * the alternative is a document filed against the wrong business, silently,
 * with the visible name looking perfectly correct.
 *
 * ⚠ String(match.id) — the id is numeric in the Autotask cache but every
 * comparison against it in the app is string-based. The one managed-filter that
 * forgot String() is a documented recurring bug in this codebase.
 */
export function companyLinkId(match) {
  return match && match.id != null ? String(match.id) : "";
}

/** A name, normalised. Never the string "null" on a record. */
function nameOf(name) {
  return name == null ? "" : String(name);
}

/**
 * QUOTE shape — both fields top level.
 * Apply straight to the quote: `updateQuote(id, quoteCompanyPatch(name, match))`.
 */
export function quoteCompanyPatch(name, match) {
  return {
    companyName: nameOf(name),
    companyId: companyLinkId(match),
  };
}

/**
 * PROPOSAL shape — SPLIT, because the two halves are written through different
 * setters. `content` goes through the panel's content writer (updateContent);
 * `patch` goes onto the record itself (patchProposal), the same way
 * opportunityId already does.
 *
 * ⚠ Returned as two objects rather than one flat patch so a caller cannot
 * accidentally spread customerName onto the record or companyId into content.
 * Both mistakes render fine and break something later.
 */
export function proposalCompanyPatch(name, match) {
  return {
    content: { customerName: nameOf(name) },
    patch: { companyId: companyLinkId(match) },
  };
}

/**
 * Does this record carry a real Autotask link?
 *
 * Drives the field's note, and — on quotes — is the same condition Push to
 * Autotask is guarded on in two places. Works for either document type, since
 * both hold the id at the top level.
 */
export function isLinkedToAutotask(record) {
  return !!(record && record.companyId);
}
